import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, UserCog } from 'lucide-react';
import type { Role, User } from '@reflex/shared';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { Button } from '@reflex/ui/components/ui/button';
import { CanGuard, usePermission } from '@reflex/ui/client/permissions';
import { AddMemberDialog } from '@/components/members/AddMemberDialog';
import { MemberRow } from '@/components/members/MemberRow';
import { MemberRolesPopover } from '@/components/members/MemberRolesPopover';
import { OrganizationInviteList } from '@/components/members/OrganizationInviteList';
import { ShareInviteDialog } from '@/components/members/ShareInviteDialog';
import { useInviteShares } from '@/components/members/useInviteShares';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { RequireActiveOrganization } from '@/components/shared/RequireActiveOrganization';
import { HubReadGuard } from '@reflex/ui/components/layout/NoReadAccessState';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { useAuth } from '@/hooks/auth/useAuth';
import { useRefreshSession } from '@/hooks/auth/useSessionRefresh';
import { useUsers } from '@/hooks/auth/useUsers';
import { api } from '@/lib/api';
import { mutateWithToast } from '@/lib/mutation-helpers';

export function OrganizationMembersPage() {
  return (
    <HubReadGuard permission="org:read">
      <RequireActiveOrganization title="Members">
        {(org) => <MembersBody key={org.id} orgId={org.id} />}
      </RequireActiveOrganization>
    </HubReadGuard>
  );
}

function MembersBody({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const refreshSession = useRefreshSession();
  const canManageRoles = can('roles:manage');
  // `usePermission`, not `can`: it applies the platform-admin and
  // auth-disabled bypasses the server applies too (`requirePermOnPathOrg`
  // returns early for platform admins), which is what the `CanGuard` around
  // the member-remove button below gets for free. A bare `can()` here would
  // hide revoke from a platform admin the DELETE would have accepted.
  const canManageMembers = usePermission('members:manage');
  // Resending an invite from the list below mints a fresh link, and the page
  // owns the one dialog that shows it.
  const { shares, addShares, clearShares } = useInviteShares();

  const membersQuery = useQuery({
    queryKey: ['organizations', orgId, 'members'],
    queryFn: () => api.listOrganizationMembers(orgId),
    enabled: !!orgId,
  });

  const usersQuery = useUsers();

  const orgRolesQuery = useQuery({
    queryKey: ['organizations', orgId, 'roles'],
    queryFn: () => api.listOrganizationRoles(orgId),
    enabled: !!orgId,
  });

  const usersById = useMemo(() => {
    const map = new Map<string, User>();
    for (const user of usersQuery.data ?? []) map.set(user.id, user);
    return map;
  }, [usersQuery.data]);

  const orgScopedRoles = useMemo<Role[]>(
    () => (orgRolesQuery.data ?? []).filter((r) => r.scope === 'org'),
    [orgRolesQuery.data],
  );

  const removeMutation = useMutation({
    mutationFn: (userId: string) => api.removeOrganizationMember(orgId, userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organizations', orgId, 'members'] });
      // Removing yourself drops the membership every `can()` gate reads.
      refreshSession();
    },
  });

  const assignRoleMutation = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      api.assignOrganizationRole(orgId, userId, roleId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organizations', orgId, 'members'] });
      refreshSession();
    },
  });

  const revokeRoleMutation = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      api.revokeOrganizationRole(orgId, userId, roleId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organizations', orgId, 'members'] });
      refreshSession();
    },
  });

  const rolesPending = assignRoleMutation.isPending || revokeRoleMutation.isPending;

  return (
    <PageLayout maxWidth="4xl">
      <PageHeader
        iconVariant="box"
        icon={<UserCog className="h-5 w-5 text-muted-foreground" />}
        title="Members"
        description="People with access to this organization."
        docs="administration/organization-teams#members"
        actions={
          <CanGuard permission="members:invite">
            <AddMemberDialog organizationId={orgId} tutorialId="invite-send" />
          </CanGuard>
        }
      />

      <SectionCard
        title="All members"
        description="Everyone currently in this organization."
        data-testid="members-section"
      >
        <AsyncContent
          isLoading={membersQuery.isLoading}
          isError={membersQuery.isError}
          loadingMessage="Loading members…"
          errorMessage="Failed to load members."
        >
          {membersQuery.data && membersQuery.data.length > 0 ? (
            <ul className="divide-y divide-border rounded-md border">
              {membersQuery.data.map((member) => {
                const user = usersById.get(member.userId);
                const removeLabel = user?.name ?? user?.login ?? member.userId;
                return (
                  <MemberRow
                    key={member.memberId}
                    user={user}
                    userIdFallback={member.userId}
                    testId={`org-member-row-${member.userId}`}
                    rightSlot={
                      <>
                        <MemberRolesPopover
                          availableRoles={orgScopedRoles}
                          assignedRoles={member.roles}
                          memberKey={member.userId}
                          pending={rolesPending}
                          readOnly={!canManageRoles}
                          onToggle={(roleId, next) => {
                            if (next) {
                              mutateWithToast(
                                assignRoleMutation,
                                { userId: member.userId, roleId },
                                {
                                  success: 'Role assigned',
                                  error: 'Failed to assign role',
                                },
                              );
                            } else {
                              mutateWithToast(
                                revokeRoleMutation,
                                { userId: member.userId, roleId },
                                {
                                  success: 'Role revoked',
                                  error: 'Failed to revoke role',
                                },
                              );
                            }
                          }}
                        />
                        <CanGuard permission="members:manage">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              mutateWithToast(removeMutation, member.userId, {
                                success: 'Member removed',
                                error: 'Failed to remove member',
                              })
                            }
                            disabled={removeMutation.isPending}
                            aria-label={`Remove ${removeLabel}`}
                          >
                            <Trash2 className="icon-sm" />
                          </Button>
                        </CanGuard>
                      </>
                    }
                  />
                );
              })}
            </ul>
          ) : (
            <EmptyState variant="bordered" title="No members yet" />
          )}
        </AsyncContent>
      </SectionCard>

      <CanGuard permission="members:invite">
        <SectionCard
          title="Open invites"
          description="Invites that have not been redeemed yet. Use Add member to send a new one."
          data-testid="invites-section"
        >
          <OrganizationInviteList
            organizationId={orgId}
            canRevoke={canManageMembers}
            onShare={(share) => addShares([share])}
          />
        </SectionCard>
      </CanGuard>

      <ShareInviteDialog shares={shares} onClose={clearShares} />
    </PageLayout>
  );
}
